/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import {
    Icon,
    FlexRow,
    FlexCol,
    Button,
    TextField,
    NumberField,
    DatePicker,
    localToday,
} from 'sarsaparilla';
import { parseDate } from '@internationalized/date';
import {
    IsFirstNameEmpty,
    IsLastNameEmpty,
    IsBirthdateEmpty,
    IsAgeEmpty,
} from '../../constants';

const propTypes = {
    index: PropTypes.number,
    removeMember: PropTypes.func,
    handleMemberInfoChange: PropTypes.func,
    member: PropTypes.shape({
        fName: PropTypes.string,
        mName: PropTypes.string,
        lName: PropTypes.string,
        birthdate: PropTypes.string,
        remark: PropTypes.string,
        age: PropTypes.number,
    }),
    focusOnNextAdd: PropTypes.bool,
    isDisabled: PropTypes.bool,
    validation: PropTypes.shape({}),
    collectBirthdate: PropTypes.bool,
    collectAge: PropTypes.bool,
    disableRemove: PropTypes.bool,
    isFirstNameRequired: PropTypes.bool,
    isLastNameRequired: PropTypes.bool,
    isAgeRequired: PropTypes.bool,
    isBirthdateRequired: PropTypes.bool,
    readOnly: PropTypes.bool,
};

const defaultProps = {
    validation: {},
    isDisabled: false,
    readOnly: false,
};

export default class GroupMemberItem extends React.Component {
    static propTypes = propTypes;

    static defaultProps = defaultProps;

    constructor(props) {
        super(props);
        this.lNameInput = React.createRef();
    }

    state = {
        datePickerValue: null,
    };

    componentDidMount() {
        this.setState({
            datePickerValue: this.getDatePickerValue(this.props.member.birthdate),
        });

        if (this.props.focusOnNextAdd && this.lNameInput.current) {
            this.lNameInput.current.focus();
        }
    }

    onClickRemove = () => {
        const index = parseInt(this.props.index, 10);
        this.props.removeMember(index);
    };

    handleChange = (event) => {
        const input = event.target.value;
        const id = event.target.id.replace(/_\d+$/, '');

        this.props.handleMemberInfoChange(this.props.index, id, input);
    };

    getDatePickerValue = (value) => {
        if (value) {
            if (value.includes('/')) {
                const [month, day, year] = value.split('/');
                return parseDate(`${year}-${month}-${day}`);
            }
        }

        return null;
    };

    handleDatePickerChange = (id, value, validation) => {
        const fauxEvent = {
            target: {
                id,
                value: '',
            },
        };

        if (value && !validation.isInvalid) {
            const { year, month, day } = value;
            const dateStr = `${String(month).padStart(2, 0)}/${String(day).padStart(2, 0)}/${String(year).padStart(4, 0)}`;

            fauxEvent.target.value = dateStr;
        }

        this.setState({
            datePickerValue: value,
        });

        this.handleChange(fauxEvent);
    };

    isDateTooFarInThePast = (dateToCompare) => {
        const MAX_PAST_DATE = localToday.subtract({ years: 120 });

        return dateToCompare.compare(MAX_PAST_DATE) < 0;
    };

    isDateInTheFuture = (dateToCompare) => {
        return dateToCompare.compare(localToday) > 0;
    };

    datePickerValidator = (value, label) => {
        const isPastDate = this.isDateTooFarInThePast(value);
        const isFutureDate = this.isDateInTheFuture(value);
        let errorMessage = '';

        if (isPastDate) {
            errorMessage = `${label} is too far in the past.`;
        }

        if (isFutureDate) {
            errorMessage = `${label} can not be in the future.`;
        }

        return {
            isInvalid: isPastDate || isFutureDate,
            errorMessage,
        };
    };

    isDisabled() {
        return (this.props.isDisabled && this.props.index === 0) || this.props.readOnly;
    }

    renderRemoveMemberButton() {
        if (this.isDisabled()) {
            return <div />;
        }

        return (
            <Button
                appearance="subtle-danger"
                iconBeforeElement={<Icon iconName="delete" />}
                onClick={this.onClickRemove}
                screenReaderTextBefore={`Delete Member #${this.props.index + 1}`}
            />
        );
    }

    renderAge() {
        if (!this.props.collectAge) {
            return null;
        }

        const fail = this.props.validation[IsAgeEmpty];
        const id = `memberAge_${this.props.index}`;

        return (
            <FlexCol md={6}>
                <div className="rec-form-item-wrap">
                    <NumberField
                        className={fail ? 'rec-error' : ''}
                        id={id}
                        isDisabled={this.isDisabled()}
                        max={999}
                        min={0}
                        onChange={(value) => this.handleChange({ target: { value, id } })}
                        step={1}
                        units="years"
                        value={this.props.member.age || 0}
                        visualLabel="Age"
                        visualLabelPosition="top"
                        isRequired={this.props.isAgeRequired}
                    />
                </div>
            </FlexCol>
        );
    }

    renderBirthdate() {
        if (!this.props.collectBirthdate) {
            return null;
        }

        const fail = this.props.validation[IsBirthdateEmpty];
        const id = `memberBirthdate_${this.props.index}`;

        return (
            <FlexCol md={6}>
                <div className="rec-form-item-wrap">
                    <DatePicker
                        style={{ display: 'inline-block' }}
                        className={fail ? 'rec-error' : ''}
                        isRequired={this.props.isBirthdateRequired}
                        isDisabled={this.isDisabled()}
                        autoOpenCalendar={false}
                        label="Birth Date"
                        id={id}
                        value={this.state.datePickerValue}
                        onChange={(val, validation) =>
                            this.handleDatePickerChange(id, val, validation)
                        }
                        validator={this.datePickerValidator}
                    />
                </div>
            </FlexCol>
        );
    }

    render() {
        const { member } = this.props;

        const failFirst = this.props.validation[IsFirstNameEmpty];
        const failLast = this.props.validation[IsLastNameEmpty];

        return (
            <div className="per-form-group-item">
                <div className="per-form-group-item-header">
                    <div className="per-form-group-item-title">
                        Member {this.props.index + 1}:{' '}
                        <strong>{`${member.fName || ''} ${member.mName || ''} ${member.lName || ''}`}</strong>
                    </div>
                    {!this.props.disableRemove ? this.renderRemoveMemberButton() : null}
                </div>
                <FlexRow>
                    <FlexCol md={6}>
                        <div className="rec-form-item-wrap">
                            <TextField
                                className={failLast ? 'rec-error' : ''}
                                isInvalid={!!failLast}
                                id={`memberLName_${this.props.index}`}
                                innerRef={this.lNameInput}
                                isDisabled={this.isDisabled()}
                                isRequired={this.props.isLastNameRequired}
                                label="Last Name"
                                onChange={this.handleChange}
                                value={member.lName || ''}
                            />
                        </div>
                    </FlexCol>
                    <FlexCol md={4}>
                        <div className="rec-form-item-wrap">
                            <TextField
                                className={failFirst ? 'rec-error' : ''}
                                isInvalid={!!failFirst}
                                id={`memberFName_${this.props.index}`}
                                isDisabled={this.isDisabled()}
                                isRequired={this.props.isFirstNameRequired}
                                label="First Name"
                                onChange={this.handleChange}
                                value={member.fName || ''}
                            />
                        </div>
                    </FlexCol>
                    <FlexCol md={2}>
                        <div className="rec-form-item-wrap">
                            <TextField
                                id={`memberMName_${this.props.index}`}
                                isDisabled={this.isDisabled()}
                                label="MI"
                                onChange={this.handleChange}
                                value={member.mName || ''}
                            />
                        </div>
                    </FlexCol>
                </FlexRow>
                <FlexRow>
                    {this.renderBirthdate()}
                    {this.renderAge()}
                    <FlexCol>
                        <div className="rec-form-item-wrap">
                            <TextField
                                id={`memberRemark_${this.props.index}`}
                                isDisabled={this.isDisabled()}
                                label="Remark"
                                onChange={this.handleChange}
                                value={member.remark || ''}
                            />
                        </div>
                    </FlexCol>
                </FlexRow>
            </div>
        );
    }
}
